// @flow

import React, { Component } from 'react';
import omit from 'lodash/fp/omit';
import pick from 'lodash/fp/pick';

import * as Drip from '../utils/drip.js';
import type { DripPayload } from '../utils/drip';
import validateEmail from '../utils/validate-email';
import { isNumeric } from '../utils/vortex/inputs';

import LabeledInput from '../components/LabeledInput';
import Page from '../components/Page';
import Section from '../components/Section';
import Button from '../components/Button';

const stringifyPayload = function(payload, numberCheck = true, separator = ', ') {
  let items = [];
  for (var key in payload) {
    let value = payload[key];
    if (numberCheck && isNumeric(value)) {
      value = parseFloat(value).toFixed(2);
    }
    items.push(`${key}: ${value}`);
  }
  // console.log(items);
  return items.join(separator);
};

const DEFAULT_FORM_STATE = {
  comments: '',
  company: '',
  email: '',
  name: '',
  phone: '',
  title: '',

  bushingKit: 'none',
  result: {},
  smr: 'none',
  specs: {},

  error: null,
  errors: {},

  isSending: false,
  isValid: false
};

const ERROR_MESSAGE = "Sorry, we weren't able to send your message.";

type Props = {
  location: { [string]: any },
};

type State = {
  comments: string,
  company: string,
  email: string,
  name: string,
  phone: string,
  title: string,

  bushingKit: string,
  result: {},
  smr: string,
  specs: {},

  error: ?string,
  errors: { [string]: string },

  isSending: boolean,
  isSuccess: boolean,
  isValid: boolean
};

class Contact extends Component<Props, State> {

  form: ?HTMLFormElement

  constructor(props: Props) {
    super(props);

    const fromState = this.props.location.state || {};
    const smr = fromState.result ? fromState.result.smr : 'none';

    this.state = {
      ...DEFAULT_FORM_STATE,
      bushingKit: fromState.bushingKit || 'none',
      isSuccess: false,
      result: fromState.result || {},
      smr: smr || 'none',
      specs: fromState.specs || {}
    };
  }

  componentWillMount() {
    this.loadEmailFromStorage();
  }

  handleChange = (key: string, value: string) => {
    // console.log('handleChange', key, value);
    this.setState({ [key]: value }, () => {
      const { isValid } = this.validate();
      this.setState({ isValid });
    });
  }

  handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    // console.log('handleSubmit');
    event.preventDefault();

    const { isValid, errors } = this.validate();

    if (!isValid) {
      this.setState({ errors });
      return;
    }

    this.setState({ isSending: true });

    const keys = [
      'bushingKit',
      'comments',
      'company',
      'email',
      'name',
      'phone',
      'smr',
      'title'
    ];

    const exclude = [
      'bushings',
      'bushingDec',
      'bushingFrac',
      'bushingKitShort',
      'series'
    ];

    const result = stringifyPayload(omit(exclude, this.state.result), true);
    const specs = stringifyPayload(this.state.specs, false);

    const payload = {
      ...pick(keys, this.state),
      result,
      specs,
      tags: ['BaartTools', 'Contact']
    };

    Drip.identify(payload).then((visitor: DripPayload) => {
      // console.log('Drip identify success', visitor);
      Drip.persistLocal(visitor, false);
      Drip.track('Contact', payload).then((tracked: DripPayload) => {
        // console.log('Drip contact success', tracked);
        this.setState({ error: null, isSuccess: true });
        this.resetForm();
      }, (error: DripPayload) => {
        // console.log('Drip contact error', error);
        this.showError();
      });
    }, (error: DripPayload) => {
      // console.log('Drip identify error', error);
      this.showError();
    });
  }

  loadEmailFromStorage = () => {
    // console.log('loadEmailFromStorage');
    if (window.Modernizr.localstorage) {
      const email = localStorage.getItem('email');
      if (email && validateEmail(email)) {
        // console.log('email', email);
        this.setState({ email });
      }
    }
  }

  resetForm = () => {
    // console.log('resetForm');
    this.setState(DEFAULT_FORM_STATE);
    this.loadEmailFromStorage();
  }

  showError = () => {
    this.setState({
      error: ERROR_MESSAGE,
      isSending: false,
      isSuccess: false
    });
  }

  userResetForm = (event: SyntheticEvent<HTMLAnchorElement>) => {
    // console.log('userResetForm');
    event.preventDefault();
    this.setState({
      ...DEFAULT_FORM_STATE,
      isSuccess: false
    });
    this.loadEmailFromStorage();
  }

  validate = () => {
    const { email, name, phone } = this.state;
    const errors = {};
    if (!validateEmail(email)) {
      errors.email = 'is not a valid email';
    }
    if (name.length === 0) {
      errors.name = 'is required';
    }
    if (phone.length === 0) {
      errors.phone = 'is required';
    }
    const isValid = Object.keys(errors).length === 0;
    // console.log('validate', isValid, errors);
    return { isValid, errors };
  }

  render() {
    const { isSending, isSuccess } = this.state;

    return (
      <Page title="Contact Sales">
        <Section light>
          <h3>Contact the Sales Team</h3>
          {
            isSuccess ? (
              <p className="instructions">
                <span>Thanks, we'll be in touch!</span>
                <br />
                <a href="/contact" onClick={this.userResetForm}>Send another message</a>.
              </p>
            ) : (
              <form className="contact limit-width" onSubmit={this.handleSubmit}>
                <p className="instructions">Fill out the form below to get in touch with our sales team. Fields marked with <strong>*</strong> are required.</p>

                <fieldset disabled={isSending}>
                  <LabeledInput name="email" type="email" required={true} placeholder="Email" value={this.state.email} onChange={this.handleChange} error={this.state.errors.email} autocomplete="email" />
                  <LabeledInput name="name" type="text" required={true} placeholder="Your Name" value={this.state.name} onChange={this.handleChange} error={this.state.errors.name} autocomplete="name" />
                  <LabeledInput name="phone" type="tel" required={true} placeholder="Phone Number" value={this.state.phone} onChange={this.handleChange} error={this.state.errors.phone} autocomplete="tel" />
                  <LabeledInput name="company" type="text" placeholder="Company" value={this.state.company} onChange={this.handleChange} autocomplete="organization" />
                  <LabeledInput name="title" type="text" placeholder="Title" value={this.state.title} onChange={this.handleChange} />
                  <LabeledInput name="comments" type="textarea" placeholder="Enter your question or comments" rows={5} value={this.state.comments} onChange={this.handleChange} />
                </fieldset>

                <Button disabled={isSending} primary block>
                  {isSending ? 'Submitting...' : 'Submit'}
                </Button>
              </form>
            )
          }
        </Section>
      </Page>
    );
  }

}

export default Contact;



// WEBPACK FOOTER //
// ./src/views/Contact.js